// Node views: every block previews as it reads in the article and is edited in
// place. Atoms (media, math, raw HTML …) open their editor when selected and go
// back to the preview when you click elsewhere. Containers (headings, lists,
// quotes, accordions …) show a small settings bar while the cursor is inside.
// Footnotes edit in a popup under their marker.
import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';
import { addRowAfter, addColumnAfter, deleteRow, deleteColumn } from '@tiptap/pm/tables';
import Document from '@tiptap/extension-document';
import { UndoRedo } from '@tiptap/extensions';
import { TableView } from '@tiptap/extension-table';
import { extensions as base } from './schema.js';
import { Icon, IconButton } from './icons.jsx';

let slug = '';
export const setMediaSlug = (s) => (slug = s);

// Whether the open post uses a media file (set by the post editor; unsaved edits count).
let mediaInUse = () => false;
export const setMediaInUse = (fn) => (mediaInUse = fn);

// The footnote editor that is open, if any: the toolbar's inline buttons act on it.
export let noteEditor = null;

// Media are files in the post's own directory; old posts also have absolute
// URLs and root-relative WordPress uploads.
export function mediaUrl(src) {
  if (!src) return '';
  if (/^(https?:)?\/\//.test(src)) return src;
  if (src.startsWith('/')) return 'https://evincism.com' + src;
  return `/media/${slug}/${encodeURIComponent(src)}`;
}
const KINDS = { image: /\.(jpe?g|png|gif|webp|avif|svg)$/i, video: /\.(mp4|webm|mov|m4v)$/i };

// ---------- shared bits ----------

// TeX rendered by MathJax 3.2.2, the version the site uses. Its stylesheet
// is added once, since nothing here runs a page typeset.
const mathReady = () =>
  MathJax.startup.promise.then(() => {
    if (!document.getElementById('MJX-SVG-styles')) document.head.append(MathJax.svgStylesheet());
  });

function Tex({ tex, display }) {
  const ref = useRef();
  useEffect(() => {
    let live = true;
    const el = ref.current;
    el.replaceChildren();
    if (!tex?.trim()) return;
    mathReady()
      .then(() => MathJax.tex2svgPromise(tex, { display: !!display }))
      .then((node) => live && el.replaceChildren(node))
      .catch((e) => live && (el.textContent = e.message));
    return () => (live = false);
  }, [tex, display]);
  return <span class="tex" ref={ref} />;
}

// Pick a file already in the post's directory, or upload one into it.
// A "Pick" button and the dialog it opens: the post's own files, or upload one
// into the post's directory. An empty block opens the dialog straight away.
// `api` and `fileUrl` point it at another directory (an author's); the default is the open post.
export function MediaPicker({ kind, value, onPick, api, fileUrl, inUse, autoOpen = true, label }) {
  const base = api || `/api/posts/${slug}`;
  const urlOf = fileUrl || mediaUrl;
  const used = inUse || mediaInUse;
  const dlg = useRef();
  const [files, setFiles] = useState(null);
  const [error, setError] = useState('');
  const open = () => {
    setError('');
    fetch(`${base}/media`)
      .then((r) => r.json())
      .then(setFiles);
    dlg.current.showModal();
  };
  useEffect(() => {
    if (!value && autoOpen) open();
  }, []);
  const pick = (f) => {
    dlg.current.close();
    onPick(f);
  };
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const r = await fetch(`${base}/media/${encodeURIComponent(file.name)}`, { method: 'PUT', body: file });
    if (!r.ok) return setError(`Upload failed: ${r.status}`);
    pick((await r.json()).name);
  };
  const remove = async (f) => {
    const inThis = used(f);
    const ask = inThis
      ? `"${f}" is still in use. Delete the file anyway? It will show as missing.`
      : `Delete "${f}" from this post's folder?`;
    if (!confirm(ask)) return;
    const r = await fetch(`${base}/media/${encodeURIComponent(f)}`, { method: 'DELETE' });
    if (!r.ok) return setError(`Delete failed: ${r.status}`);
    setFiles(files.filter((x) => x !== f));
  };
  const list = (files || []).filter((f) => KINDS[kind].test(f));
  return (
    <div class="picker">
      <button type="button" class="pick-btn" onClick={open}>
        <Icon name={kind} />
        {label || (value ? `Change ${kind}` : `Pick ${kind}`)}
      </button>
      {value && <span class="hint picked">{value}</span>}
      <dialog
        ref={dlg}
        class="picker-dialog"
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.target === dlg.current && dlg.current.close()}
      >
        <header>
          <b>Choose {kind === 'image' ? 'a picture' : 'a video'}</b>
          <label class="primary upload-btn">
            <Icon name="upload" /> Upload new
            <input type="file" accept={kind + '/*'} onChange={upload} />
          </label>
          <IconButton icon="close" title="Close" run={() => dlg.current.close()} />
        </header>
        {error && <p class="error">{error}</p>}
        <div class={'picker-grid' + (kind === 'image' ? '' : ' files')}>
          {files && !list.length && <p class="hint">No {kind} files in this post yet. Upload one.</p>}
          {list.map((f) => (
            <div class={'tile' + (f === value ? ' on' : '')}>
              <button type="button" title={f} onClick={() => pick(f)}>
                {kind === 'image' ? <img src={urlOf(f)} alt="" loading="lazy" /> : <Icon name="video" />}
                <span>{f}</span>
              </button>
              <IconButton class="tile-del" icon="trash" title={'Delete ' + f} run={() => remove(f)} />
            </div>
          ))}
        </div>
      </dialog>
    </div>
  );
}

// One attribute input. type is 'text', 'number', 'bool' or an array of options.
function Field({ attrs, set, k, type, placeholder }) {
  if (type === 'bool')
    return <input type="checkbox" checked={!!attrs[k]} onChange={(e) => set({ [k]: e.target.checked })} />;
  if (Array.isArray(type))
    return (
      <select value={attrs[k] ?? ''} onChange={(e) => set({ [k]: e.target.value })}>
        {type.map((o) => (
          <option value={o}>{o || 'none'}</option>
        ))}
      </select>
    );
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={attrs[k] ?? ''}
      onInput={(e) => set({ [k]: type === 'number' && e.target.value ? Number(e.target.value) : e.target.value })}
    />
  );
}

// Inline labelled inputs, for the settings bars. `list` is [attr, type, label?].
function Fields({ attrs, set, list }) {
  return (
    <span class="fields">
      {list.map(([k, type, label]) => (
        <label>
          <span>{label || k}</span>
          <Field attrs={attrs} set={set} k={k} type={type} />
        </label>
      ))}
    </span>
  );
}

// A form of rows; each row is a list of [attr, type, label, placeholder?] sharing the width.
export function Form({ attrs, set, rows }) {
  return (
    <div class="form">
      {rows.map((row) => (
        <div class="form-row" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
          {row.map(([k, type, label, placeholder]) => (
            <label>
              <span>{label}</span>
              <Field attrs={attrs} set={set} k={k} type={type} placeholder={placeholder} />
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function AutoFocus({ children }) {
  const ref = useRef();
  useEffect(() => ref.current.querySelector('textarea, input:not([type=file]):not([type=checkbox])')?.focus(), []);
  return <span ref={ref}>{children}</span>;
}

const youtubeId = (url) => ((url || '').match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([\w-]{6,})/) || [])[1];
export const empty = (text) => <div class="empty">{text}</div>;

// ---------- atoms ----------

const atoms = {
  image: ({ attrs: a, editing, set, editor, getPos }) => (
    <>
      {a.src ? (
        <figure class={'v-image' + (a.align ? ' align-' + a.align : '')}>
          <img
            src={mediaUrl(a.src)}
            alt={a.alt}
            referrerpolicy="no-referrer"
            width={a.width || undefined}
            height={a.height || undefined}
          />
        </figure>
      ) : (
        empty('No picture. Click to pick one.')
      )}
      {editing && (
        <div class="edit">
          <MediaPicker kind="image" value={a.src} onPick={(src) => set({ src })} />
          <Form
            attrs={a}
            set={set}
            rows={[
              [['alt', 'text', 'Alt text', 'Describe the picture']],
              [
                ['height', 'text', 'Height', 'auto'],
                ['width', 'text', 'Width', 'auto'],
                ['align', ['', 'left', 'center'], 'Align'],
              ],
              [['href', 'text', 'Link', 'https://…']],
            ]}
          />
        </div>
      )}
    </>
  ),
  video: ({ attrs: a, editing, set, editor, getPos }) => (
    <>
      {a.src ? (
        <video class="v-video" controls preload="metadata" src={mediaUrl(a.src)} />
      ) : (
        empty('No video. Click to pick one.')
      )}
      {editing && (
        <div class="edit">
          <MediaPicker kind="video" value={a.src} onPick={(src) => set({ src })} />
        </div>
      )}
    </>
  ),
  embed: ({ attrs: a, editing, set, editor, getPos }) => {
    const id = youtubeId(a.url);
    return (
      <>
        {id ? (
          <div class="v-embed">
            <iframe src={`https://www.youtube-nocookie.com/embed/${id}`} title="YouTube video" allowFullScreen />
          </div>
        ) : a.url ? (
          <a href={a.url}>{a.url}</a>
        ) : (
          empty('No video URL. Click to add one.')
        )}
        {editing && (
          <div class="edit">
            <AutoFocus>
              <Form attrs={a} set={set} rows={[[['url', 'text', 'YouTube URL', 'https://youtu.be/…']]]} />
            </AutoFocus>
          </div>
        )}
      </>
    );
  },
  mathBlock: ({ attrs: a, editing, set }) => (
    <>
      {a.tex ? (
        <div class="v-math">
          <Tex tex={a.tex} display />
        </div>
      ) : (
        !editing && empty('No math. Click to write TeX.')
      )}
      {editing && (
        <div class="edit">
          <AutoFocus>
            <textarea
              class="code"
              rows={3}
              placeholder="TeX, e.g. F = -\frac{G m_1 m_2}{r^2}"
              value={a.tex}
              onInput={(e) => set({ tex: e.target.value })}
            />
          </AutoFocus>
        </div>
      )}
    </>
  ),
  rawHtml: ({ attrs: a, editing, set }) => (
    <>
      {a.html ? (
        <iframe class="v-raw" sandbox="" srcdoc={a.html} title="raw HTML" />
      ) : (
        !editing && empty('No HTML. Click to add some.')
      )}
      {editing && (
        <div class="edit">
          <AutoFocus>
            <textarea class="code" rows={6} value={a.html} onInput={(e) => set({ html: e.target.value })} />
          </AutoFocus>
        </div>
      )}
    </>
  ),
  mathInline: ({ attrs: a, editing, set }) => (
    <>
      {a.tex ? <Tex tex={a.tex} /> : <span class="empty">math</span>}
      {editing && (
        <span class="edit popup">
          <AutoFocus>
            <input class="code" placeholder="TeX" value={a.tex} onInput={(e) => set({ tex: e.target.value })} />
          </AutoFocus>
        </span>
      )}
    </>
  ),
  footnote: ({ node, editing, editor, getPos }) => (
    <>
      <span class="fn-mark" title={node.textContent} />
      {editing && (
        <span class="edit popup">
          <NoteEditor node={node} editor={editor} getPos={getPos} />
        </span>
      )}
    </>
  ),
};

// The note text of a footnote, edited in a small editor of its own.
function NoteEditor({ node, editor, getPos }) {
  const ref = useRef();
  useEffect(() => {
    const inner = new Editor({
      element: ref.current,
      extensions: [
        // a note is one paragraph: Enter starts a new line in it
        Document.extend({
          content: 'paragraph',
          addKeyboardShortcuts: () => ({ Enter: ({ editor: e }) => e.commands.setHardBreak() }),
        }),
        ...extensions.filter((e) =>
          [
            'text',
            'paragraph',
            'bold',
            'italic',
            'link',
            'superscript',
            'subscript',
            'mathInline',
            'hardBreak',
          ].includes(e.name)
        ),
        UndoRedo,
      ],
      content: { type: 'doc', content: [{ type: 'paragraph', content: node.content.toJSON() || [] }] },
      onUpdate: ({ editor: e }) => {
        const pos = getPos();
        const { state } = editor;
        const content = Fragment.fromJSON(state.schema, e.getJSON().content[0].content || []);
        const fn = state.schema.nodes.footnote.create(null, content);
        const tr = state.tr.replaceWith(pos, pos + state.doc.nodeAt(pos).nodeSize, fn);
        tr.setSelection(NodeSelection.create(tr.doc, pos));
        editor.view.dispatch(tr);
      },
      // let the toolbar show the marks under the note editor's cursor
      onSelectionUpdate: () => editor.view.dispatch(editor.state.tr.setMeta('noteSelection', true)),
    });
    noteEditor = inner;
    inner.commands.focus('end');
    return () => {
      if (noteEditor === inner) noteEditor = null;
      inner.destroy();
    };
  }, []);
  return <span class="note" ref={ref} />;
}

// Whether a mouse button is down (a click is in progress).
let pointerDown = false;
document.addEventListener('mousedown', () => (pointerDown = true), true);
document.addEventListener('mouseup', () => setTimeout(() => (pointerDown = false)), true);

// Clicks outside an open atom close its editor; clicks on the toolbar do not.
const keepsEditing = (target) => target.closest?.('.toolbar');

function atomView(name, View) {
  return () =>
    ({ node, editor, getPos }) => {
      let cur = node;
      let editing = false;
      const tag = name === 'footnote' ? 'sup' : node.type.isInline ? 'span' : 'div';
      const dom = document.createElement(tag);
      dom.className = 'b-' + name;
      dom.dataset.type = name;
      const set = (attrs) => {
        const pos = getPos();
        const next = { ...cur.attrs };
        for (const [k, v] of Object.entries(attrs)) next[k] = v === '' ? (cur.type.spec.attrs[k].default ?? null) : v;
        const tr = editor.state.tr.setNodeMarkup(pos, undefined, next);
        tr.setSelection(NodeSelection.create(tr.doc, pos));
        editor.view.dispatch(tr);
      };
      const draw = () => {
        dom.classList.toggle('editing', editing);
        // a figure's media goes with the figure: only the figure has a trash icon
        const inFigure = () => editor.state.doc.resolve(getPos()).parent.type.name === 'figure';
        const remove = () =>
          editor
            .chain()
            .focus()
            .deleteRange({ from: getPos(), to: getPos() + cur.nodeSize })
            .run();
        render(
          <>
            {editing && !cur.type.isInline && !inFigure() && <Trash name={name} remove={remove} />}
            <View node={cur} attrs={cur.attrs} editing={editing} set={set} editor={editor} getPos={getPos} />
          </>,
          dom
        );
      };
      const close = () => {
        if (!editing) return;
        editing = false;
        draw();
      };
      // Opening is on mousedown; closing waits for the click, so the panel
      // collapsing does not move the content under the pointer mid-click.
      const onDown = (e) => {
        if (dom.contains(e.target) && !editing && editor.state.selection.node === cur) ((editing = true), draw());
      };
      const onClick = (e) => !dom.contains(e.target) && !keepsEditing(e.target) && close();
      const onKey = (e) => {
        if (e.key !== 'Escape' || !editing) return;
        close();
        editor.commands.focus();
      };
      document.addEventListener('mousedown', onDown, true);
      document.addEventListener('click', onClick, true);
      dom.addEventListener('keydown', onKey);
      draw();
      return {
        dom,
        update(n) {
          if (n.type !== cur.type) return false;
          cur = n;
          draw();
          return true;
        },
        selectNode() {
          dom.classList.add('ProseMirror-selectednode');
          editing = true;
          draw();
        },
        deselectNode() {
          dom.classList.remove('ProseMirror-selectednode');
          // mid-click, wait for the button to come up (see onClick)
          if (pointerDown) document.addEventListener('mouseup', () => setTimeout(close), { once: true });
          else close();
        },
        stopEvent: (e) => !!e.target.closest?.('.edit, .trash'),
        ignoreMutation: () => true,
        destroy() {
          document.removeEventListener('mousedown', onDown, true);
          document.removeEventListener('click', onClick, true);
          render(null, dom);
        },
      };
    };
}

// ---------- containers ----------

// A heading's anchor id, edited in a small popup opened from a settings icon.
function HeadingBar({ node, set }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const input = useRef();
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const down = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', down, true);
    return () => document.removeEventListener('mousedown', down, true);
  }, [open]);
  const id = node.attrs.id;
  return (
    <span class="bar-pop" ref={ref}>
      <IconButton icon="settings" title="Anchor id" active={open || !!id} run={() => setOpen(!open)} />
      {id && !open && <span class="anchor">#{id}</span>}
      {open && (
        <span class="link-popup anchor-pop">
          <span class="hint">Anchor #</span>
          <input
            ref={input}
            placeholder="none"
            value={id || ''}
            onInput={(e) => set({ id: e.target.value.replace(/[^\w-]+/g, '-') })}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && (e.preventDefault(), setOpen(false))}
          />
        </span>
      )}
    </span>
  );
}

// Settings bars. run(cmd) calls cmd(state, dispatch, pos), pos being the node's position.
const bars = {
  heading: ({ node, set }) => <HeadingBar node={node} set={set} />,
  orderedList: ({ node, set }) => <Fields attrs={node.attrs} set={set} list={[['start', 'number', 'starts at']]} />,
  blockquote: ({ node, run }) => {
    const end = (pos) => pos + node.nodeSize - 1;
    const has = node.lastChild.type.name === 'cite';
    return (
      <IconButton
        icon="cite"
        title={has ? 'Remove the citation' : 'Add a citation'}
        active={has}
        run={() =>
          run((st, d, pos) =>
            d(
              has
                ? st.tr.delete(end(pos) - node.lastChild.nodeSize, end(pos))
                : st.tr.insert(end(pos), st.schema.nodes.cite.create())
            )
          )
        }
      />
    );
  },
  details: ({ node, set }) => (
    <Fields
      attrs={{ ...node.attrs, faq: node.attrs.schema === 'faq' }}
      set={(a) => set('faq' in a ? { schema: a.faq ? 'faq' : null } : a)}
      list={[
        ['faq', 'bool', 'FAQ schema'],
        ['open', 'bool', 'open'],
        ['titleTag', ['', 'h2', 'h3', 'h4', 'h5'], 'title tag'],
      ]}
    />
  ),
  tweet: ({ node, set }) => <Fields attrs={node.attrs} set={set} list={[['url', 'text', 'tweet URL']]} />,
  gallery: ({ node, set, run }) => {
    const end = (pos) => pos + node.nodeSize - 1;
    const has = node.lastChild.type.name === 'caption';
    return (
      <>
        <Fields attrs={node.attrs} set={set} list={[['columns', 'number']]} />
        <IconButton
          icon="imageAdd"
          title="Add an image"
          run={() =>
            run((st, d, pos) => {
              const at = has ? end(pos) - node.lastChild.nodeSize : end(pos);
              const tr = st.tr.insert(at, st.schema.nodes.image.create());
              d(tr.setSelection(NodeSelection.create(tr.doc, at)));
            })
          }
        />
      </>
    );
  },
  figure: () => null,
  table: ({ node, run }) => (
    <>
      <IconButton icon="rowAdd" title="Add a row below" run={() => run(addRowAfter)} />
      <IconButton icon="rowDel" title="Delete this row" run={() => run(deleteRow)} />
      <IconButton icon="colAdd" title="Add a column to the right" run={() => run(addColumnAfter)} />
      <IconButton icon="colDel" title="Delete this column" run={() => run(deleteColumn)} />
      <IconButton
        icon="header"
        title="First row as header on/off"
        active={node.firstChild.firstChild.type.name === 'tableHeader'}
        run={() => run(toggleFirstRowHeader)}
      />
    </>
  ),
};

const NAMES = {
  heading: 'heading',
  orderedList: 'numbered list',
  blockquote: 'quote',
  details: 'accordion',
  tweet: 'tweet',
  gallery: 'gallery',
  figure: 'figure',
  table: 'table',
  image: 'image',
  video: 'video',
  embed: 'YouTube video',
  mathBlock: 'math',
  rawHtml: 'HTML',
};

// The trash icon floating at a block's top-left corner.
const Trash = ({ name, remove }) => (
  <IconButton class="trash" icon="trash" title={'Delete this ' + NAMES[name]} run={remove} />
);

// The header row is always the first row (GFM).
const toggleFirstRowHeader = (state, dispatch, pos) => {
  const row = state.doc.nodeAt(pos).firstChild;
  const { tableHeader, tableCell } = state.schema.nodes;
  const type = row.firstChild.type === tableHeader ? tableCell : tableHeader;
  const tr = state.tr;
  row.forEach((cell, off) => tr.setNodeMarkup(pos + 2 + off, type, cell.attrs));
  dispatch(tr);
};

const contentTags = {
  heading: (n) => 'h' + n.attrs.level,
  orderedList: () => 'ol',
  blockquote: () => 'blockquote',
  details: () => 'div',
  tweet: () => 'div',
  gallery: () => 'div',
  figure: () => 'div',
};

function containerView(name) {
  const Bar = bars[name];
  return () =>
    ({ node, editor, getPos }) => {
      let cur = node;
      const dom = document.createElement('div');
      dom.className = 'b-' + name;
      dom.dataset.type = name;
      const bar = document.createElement('div');
      bar.className = 'blockbar';
      bar.contentEditable = 'false';
      let table = null;
      let content;
      if (name === 'table') {
        table = new TableView(node, 25);
        content = table.contentDOM;
        dom.append(bar, table.dom);
      } else {
        content = document.createElement(contentTags[name](node));
        content.className = 'b-content';
        dom.append(bar, content);
      }
      const apply = () => {
        if (name === 'orderedList') content.start = cur.attrs.start ?? 1;
        if (name === 'gallery') dom.style.setProperty('--columns', cur.attrs.columns || 3);
      };
      const set = (attrs) => {
        const next = { ...cur.attrs };
        for (const [k, v] of Object.entries(attrs)) next[k] = v === '' ? (cur.type.spec.attrs[k].default ?? null) : v;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(getPos(), undefined, next));
      };
      const run = (cmd) => cmd(editor.state, editor.view.dispatch, getPos());
      const sync = () => {
        const pos = getPos();
        if (pos == null || !editor.view.dom.contains(dom)) return;
        const sel = editor.state.selection;
        const { from, to } = sel;
        const active = from > pos && to < pos + cur.nodeSize;
        // a selected block inside shows its own trash icon (except a figure's media)
        const inner = sel instanceof NodeSelection && active && !(name === 'figure' && sel.$from.parent === cur);
        dom.classList.toggle('active', active);
        render(
          active ? (
            <>
              {!inner && (
                <Trash
                  name={name}
                  remove={() =>
                    editor
                      .chain()
                      .focus()
                      .deleteRange({ from: getPos(), to: getPos() + cur.nodeSize })
                      .run()
                  }
                />
              )}
              <span class="bb-name">{NAMES[name]}</span>
              <Bar node={cur} set={set} run={run} />
            </>
          ) : null,
          bar
        );
      };
      apply();
      editor.on('transaction', sync);
      queueMicrotask(sync);
      return {
        dom,
        contentDOM: content,
        update(n) {
          if (n.type !== cur.type) return false;
          if (name === 'heading' && n.attrs.level !== cur.attrs.level) return false;
          cur = n;
          table?.update(n);
          apply();
          sync();
          return true;
        },
        stopEvent: (e) => bar.contains(e.target),
        ignoreMutation: (m) =>
          bar.contains(m.target) ||
          (m.type === 'attributes' && (m.target === dom || m.target === content || m.target === table?.table)) ||
          !!table?.ignoreMutation?.(m),
        destroy() {
          editor.off('transaction', sync);
          render(null, bar);
        },
      };
    };
}

export const extensions = base.map((ext) => {
  if (atoms[ext.name]) return ext.extend({ addNodeView: atomView(ext.name, atoms[ext.name]) });
  if (bars[ext.name]) return ext.extend({ addNodeView: containerView(ext.name) });
  return ext;
});
